export const getSnapshotErrorMessage = (error) => {
    const errorMessage = error?.message?.toLowerCase() || '';

    if (errorMessage.includes('video element not found') || errorMessage.includes('queryselector')) {
        return 'Camera stream is not available. Please ensure cameras are connected and streaming.';
    }

    if (errorMessage.includes('video dimensions not available') || errorMessage.includes('stream may not be active')) {
        return 'Unable to capture image. Camera stream may not be active yet. Please wait for the stream to load and try again.';
    }

    return 'Failed to capture camera images. Please check camera streams and try again.';
};

export const hasCameraTypes = (camsConfig, types) => {
    if (!camsConfig) return false;
    for (const levelKey of Object.keys(camsConfig)) {
        const cameras = camsConfig[levelKey];
        if (!Array.isArray(cameras)) continue;
        const hasType = cameras.some((cam) => types.includes(cam.type) && !cam.hidden);
        if (hasType) return true;
    }
    return false;
};

export const findCameraByType = (camsConfig, cameraType) => {
    if (!camsConfig) return null;
    for (const levelKey of Object.keys(camsConfig)) {
        const cameras = camsConfig[levelKey];
        if (!Array.isArray(cameras)) continue;
        const camera = cameras.find((cam) => cam.type === cameraType && !cam.hidden);
        if (camera) return camera;
    }
    return null;
};

export const captureSnapshotFromCamera = (cameraIdOrElementId) => {
    return new Promise((resolve, reject) => {
        try {
            let videoElement = document.getElementById(cameraIdOrElementId);

            if (!videoElement) {
                videoElement = document.querySelector(`video[data-camera-id="${cameraIdOrElementId}"]`);
            }

            if (!videoElement) {
                try {
                    videoElement = document.querySelector(`#camera-${cameraIdOrElementId} video`);
                } catch (error) {
                    console.error(
                        `[AWS Rekognition Snapshot] Selector #camera-${cameraIdOrElementId} video failed due to special characters:`,
                        error
                    );
                }
            }

            if (!videoElement) {
                try {
                    videoElement = document.querySelector(`[id*="${cameraIdOrElementId}"] video`);
                } catch (error) {
                    console.error(
                        `[AWS Rekognition Snapshot] Selector [id*="${cameraIdOrElementId}"] video failed due to special characters:`,
                        error
                    );
                }
            }

            if (!videoElement) {
                try {
                    videoElement = document.querySelector(`video[id$="-${cameraIdOrElementId}"]`);
                } catch (error) {
                    console.error(
                        `[AWS Rekognition Snapshot] Selector video[id$="-${cameraIdOrElementId}"] failed due to special characters:`,
                        error
                    );
                }
            }

            if (!videoElement) {
                const allVideoElements = document.querySelectorAll('video');
                for (const vid of allVideoElements) {
                    if (vid.id && vid.id.endsWith(`-${cameraIdOrElementId}`)) {
                        videoElement = vid;
                        break;
                    }
                }
            }

            if (!videoElement) {
                reject(new Error(`Video element not found for camera: ${cameraIdOrElementId}`));
                return;
            }

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            const intrinsicWidth = videoElement.videoWidth;
            const intrinsicHeight = videoElement.videoHeight;

            if (!intrinsicWidth || !intrinsicHeight) {
                reject(new Error('Video dimensions not available - stream may not be active'));
                return;
            }

            canvas.width = intrinsicWidth;
            canvas.height = intrinsicHeight;

            let angle = 0;
            const style = videoElement.getAttribute('style');
            if (style) {
                const angleMatch = style.match(/rotate\((-?\d+)deg\)/);
                if (angleMatch && angleMatch[1]) {
                    angle = parseInt(angleMatch[1], 10);
                }
            }

            if (angle) {
                if (angle === 270 || angle === 90) {
                    canvas.width = intrinsicHeight;
                    canvas.height = intrinsicWidth;
                }
                context.translate(canvas.width / 2, canvas.height / 2);
                context.rotate((angle * Math.PI) / 180);
                context.translate(-intrinsicWidth / 2, -intrinsicHeight / 2);
            }

            context.drawImage(videoElement, 0, 0, intrinsicWidth, intrinsicHeight);

            const base64 = canvas.toDataURL('image/jpeg', 0.9);
            const base64Data = base64.split(',')[1];

            canvas.width = 0;
            canvas.height = 0;

            resolve(base64Data);
        } catch (error) {
            reject(error);
        }
    });
};
